import { useMemo, useState } from 'react'
import {
  searchAwardsByFilm,
  summarizeFilmHits,
  type AwardsIndex,
  type FilmSearchHit,
} from './awards'
import { useTmdbLazyMovie } from '../library/useTmdbLazy'
import { LazyImage } from '../ui'

interface Props {
  index: AwardsIndex
  onOpenDetail: (tmdbId: number, type: 'movie' | 'tv') => void
}

/**
 * "Search by film" — type a title, see every award / nomination the
 * film has received across every body and every ceremony. Mirrors the
 * desktop feature one-for-one: a summary card per matching film at the
 * top and a detailed row list underneath grouped by film and year.
 */
export function SearchByFilm({ index, onOpenDetail }: Props) {
  const [query, setQuery] = useState('')

  const hits = useMemo(
    () => (query.trim().length >= 2 ? searchAwardsByFilm(index, query) : []),
    [index, query]
  )
  const summaries = useMemo(() => summarizeFilmHits(hits), [hits])

  // Group detailed hits by tmdbId (or title fallback) so the user can see
  // all ceremonies the film was cited in under one header.
  const grouped = useMemo(() => {
    const map = new Map<string, FilmSearchHit[]>()
    for (const h of hits) {
      const key = h.tmdbId != null ? `id:${h.tmdbId}` : `name:${h.title.toLowerCase()}`
      if (!map.has(key)) map.set(key, [])
      map.get(key)!.push(h)
    }
    // Ensure each group's rows are sorted newest-first by year.
    for (const arr of map.values()) {
      arr.sort((a, b) => b.year - a.year)
    }
    return map
  }, [hits])

  return (
    <div className="space-y-6">
      <div className="relative">
        <label htmlFor="awards-film-search" className="sr-only">
          Search film
        </label>
        <svg
          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-tertiary"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <circle cx="11" cy="11" r="8" />
          <path d="M21 21l-4.35-4.35" />
        </svg>
        <input
          id="awards-film-search"
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search any film by title across every award body…"
          className="w-full rounded-xl border border-border-primary bg-bg-secondary px-10 py-3 text-sm text-text-primary placeholder:text-text-tertiary focus:border-accent-primary focus:outline-none"
        />
        {query && (
          <button
            type="button"
            onClick={() => setQuery('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 rounded text-xs text-text-tertiary hover:text-text-primary"
            aria-label="Clear search"
          >
            Clear
          </button>
        )}
      </div>

      {query.trim().length < 2 && (
        <div className="rounded-xl border border-dashed border-border-primary bg-bg-secondary/40 p-10 text-center text-sm text-text-tertiary">
          Type at least 2 characters to search awards by film.
        </div>
      )}

      {query.trim().length >= 2 && hits.length === 0 && (
        <div className="rounded-xl border border-dashed border-border-primary bg-bg-secondary/40 p-10 text-center text-sm text-text-tertiary">
          No award rows match &ldquo;{query}&rdquo;.
        </div>
      )}

      {summaries.length > 0 && (
        <section>
          <h3 className="mb-3 text-xs font-bold uppercase tracking-wider text-text-tertiary">
            {summaries.length}{' '}
            {summaries.length === 1 ? 'matching film' : 'matching films'}
          </h3>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {summaries.slice(0, 30).map((sum) => {
              const groupKey =
                sum.tmdbId != null
                  ? `id:${sum.tmdbId}`
                  : `name:${sum.title.toLowerCase()}`
              const rows = grouped.get(groupKey) ?? []
              return (
                <FilmSummaryCard
                  key={groupKey}
                  summary={sum}
                  rows={rows}
                  onOpen={() =>
                    sum.tmdbId != null && onOpenDetail(sum.tmdbId, 'movie')
                  }
                />
              )
            })}
          </div>
        </section>
      )}
    </div>
  )
}

function FilmSummaryCard({
  summary,
  rows,
  onOpen,
}: {
  summary: {
    tmdbId: number | null
    title: string
    wins: number
    nominations: number
  }
  rows: FilmSearchHit[]
  onOpen: () => void
}) {
  const hasTmdb = summary.tmdbId != null
  const lazy = useTmdbLazyMovie(hasTmdb ? summary.tmdbId! : 0)
  const tmdb = hasTmdb ? lazy.details : undefined
  const title = tmdb?.title ?? summary.title
  const year = tmdb?.year
  const poster = tmdb?.posterUrl

  return (
    <article
      ref={hasTmdb ? (lazy.ref as (el: HTMLElement | null) => void) : undefined}
      className="rounded-xl border border-border-primary bg-bg-secondary p-4 shadow-cinematic"
    >
      <div className="flex items-start gap-3">
        <button
          type="button"
          onClick={onOpen}
          disabled={!hasTmdb}
          className="h-24 w-16 shrink-0 overflow-hidden rounded border border-border-primary bg-bg-tertiary disabled:cursor-default"
        >
          {poster ? (
            <LazyImage
              src={poster}
              alt={title}
              className="h-full w-full"
              placeholderColor="#1a1a1a"
              fallback={
                <div className="grid h-full w-full place-items-center px-1 text-center text-[9px] text-text-tertiary">
                  {title.slice(0, 20)}
                </div>
              }
            />
          ) : (
            <div className="grid h-full w-full place-items-center px-1 text-center text-[9px] text-text-tertiary">
              {title.slice(0, 20)}
            </div>
          )}
        </button>
        <div className="min-w-0 flex-1">
          <button
            type="button"
            onClick={onOpen}
            disabled={!hasTmdb}
            className="text-left disabled:cursor-default"
          >
            <h4 className="line-clamp-2 text-sm font-bold text-text-primary hover:text-accent-primary">
              {title}
            </h4>
          </button>
          {year && <p className="text-[11px] text-accent-gold">{year}</p>}
          <div className="mt-2 flex gap-2 text-[11px]">
            <span className="rounded bg-accent-gold/15 px-1.5 py-0.5 font-semibold text-accent-gold">
              {summary.wins} wins
            </span>
            <span className="rounded bg-white/5 px-1.5 py-0.5 font-semibold text-text-secondary">
              {summary.nominations} noms
            </span>
          </div>
        </div>
      </div>

      {rows.length > 0 && (
        <ul className="mt-3 space-y-1 border-t border-white/5 pt-3 text-[11px]">
          {rows.slice(0, 8).map((r, i) => (
            <li
              key={`${r.editionId}-${i}`}
              className="flex items-start gap-2"
            >
              <span
                className={`mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded ${
                  r.isWinner
                    ? 'bg-accent-gold/80 text-black'
                    : 'bg-white/10 text-text-tertiary'
                }`}
                aria-label={r.isWinner ? 'Winner' : 'Nominee'}
                title={r.isWinner ? 'Winner' : 'Nominee'}
              >
                {r.isWinner ? (
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                    <path d="M12 2l2.39 4.84 5.34.78-3.87 3.77.91 5.32L12 14.2l-4.77 2.51.91-5.32L4.27 7.62l5.34-.78L12 2z" />
                  </svg>
                ) : (
                  <span className="text-[9px]">·</span>
                )}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 truncate font-medium text-text-primary">
                  <span>{r.bodyName}</span>
                  <span className="text-text-tertiary">·</span>
                  <span className="tabular-nums text-accent-gold">{r.year}</span>
                </div>
                <div className="truncate text-text-tertiary">
                  {r.categoryName}
                </div>
              </div>
            </li>
          ))}
          {rows.length > 8 && (
            <li className="pl-6 text-text-tertiary">
              + {rows.length - 8} more
            </li>
          )}
        </ul>
      )}
    </article>
  )
}
